/**
 * FireCore Dashboard – Kennzahlen, Karte und aktuelle Ereignisse
 */

import React, { Component } from 'react';
import SecuredZone from '../components/SecuredZone';
import API from '../api/api';
import Card from '../components/Card';
import LinearProgress from '@mui/material/LinearProgress';
import { Map, Marker, ZoomControl } from 'pigeon-maps';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { Icon, IconWell } from '../icons/AppIcons';
import { ONLINE_THRESHOLD_MS } from '../utils/deviceConstants';

const FireApi = new API();

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      devices: [],
      events: [],
      locations: [],
      openAlarms: 0,
    };
  }

  componentDidMount() {
    this.loadAll();
    this.interval = setInterval(() => this.loadAll(true), 10000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  loadAll(silent = false) {
    if (!silent) this.setState({ loading: true });

    Promise.all([
      FireApi.endpoints.devicesStatus.getAll(
        { withCredentials: true, crossDomain: true },
        { page: 1, page_size: 500 }
      ),
      FireApi.endpoints.events.getAll(
        { withCredentials: true, crossDomain: true },
        { page: 1, page_size: 8 }
      ),
      FireApi.endpoints.events.getAll(
        { withCredentials: true, crossDomain: true },
        { page: 1, page_size: 1, confirmed: 0 }
      ),
    ])
      .then(([devicesRes, eventsRes, openRes]) => {
        let devices = [];
        if (devicesRes?.data?.results) devices = devicesRes.data.results;
        else if (Array.isArray(devicesRes?.data)) devices = devicesRes.data;

        let events = [];
        if (eventsRes?.data?.results) events = eventsRes.data.results;
        else if (Array.isArray(eventsRes?.data)) events = eventsRes.data;

        const openAlarms = openRes?.data?.count
          ?? events.filter((ev) => !ev.confirmed).length;

        const locations = devices
          .filter((d) => d && d.lat && d.long)
          .map((d) => ({
            id: d.id,
            name: d.name,
            latLng: [parseFloat(d.lat), parseFloat(d.long)],
            active: d.active,
            online: d.value_created
              ? Date.now() - new Date(d.value_created).getTime() < ONLINE_THRESHOLD_MS
              : false,
          }));

        this.setState({ devices, events, locations, openAlarms, loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  }

  render() {
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
    const darkTileProvider = (x, y, z, dpr) =>
      `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/${z}/${x}/${y}${dpr >= 2 ? '@2x' : ''}.png`;
    const lightTileProvider = (x, y, z, dpr) =>
      `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

    const onlineCount = this.state.devices.filter((d) =>
      d.value_created && Date.now() - new Date(d.value_created).getTime() < ONLINE_THRESHOLD_MS
    ).length;
    const offlineCount = this.state.devices.length - onlineCount;

    const kpis = [
      { to: '/devices', icon: 'koffer', tone: 'accent', value: this.state.devices.length, label: 'Koffer' },
      { to: '/devices', icon: 'online', tone: 'ok', value: onlineCount, label: 'Online' },
      { to: '/devices', icon: 'offline', tone: 'mute', value: offlineCount, label: 'Offline' },
      { to: '/events', icon: 'alarm', tone: this.state.openAlarms > 0 ? 'bad' : 'ok', value: this.state.openAlarms, label: 'Offene Alarme' },
    ];

    return (
      <SecuredZone
        breadcrumbs={{ '/': 'Dashboard' }}
        title="Dashboard"
        loading={this.state.loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
      >
        {kpis.map((kpi) => (
          <Card
            key={kpi.label}
            width="1-5"
            plain
            data={
              <Link to={kpi.to} className="dash-kpi">
                <IconWell name={kpi.icon} tone={kpi.tone} size={40} />
                <div>
                  <div className={`dash-kpi-value${kpi.tone === 'ok' ? ' is-ok' : ''}${kpi.tone === 'bad' ? ' is-bad' : ''}`}>
                    {kpi.value}
                  </div>
                  <div className="dash-kpi-label">{kpi.label}</div>
                </div>
              </Link>
            }
          />
        ))}

        <Card
          width="2-3"
          title="Standorte"
          icon="location"
          data={
            <div className="map-display-dashboard">
              <Map
                metaWheelZoom
                metaWheelZoomWarning="Verwenden Sie META+Maus-Rad"
                style={{ height: '100%' }}
                defaultCenter={[46.495349, 9.839747]}
                defaultZoom={13}
                provider={isDarkMode ? darkTileProvider : lightTileProvider}
                dprs={[1, 2]}
              >
                <ZoomControl />
                {this.state.locations.map((loc) => (
                  <Marker key={loc.id} width={30} anchor={loc.latLng}>
                    <div className={`device-map-marker ${loc.online ? 'online' : 'offline'}`}>
                      <span className="device-map-marker-name">{loc.name || ''}</span>
                      <span className="device-map-marker-status">{loc.online ? 'Online' : 'Offline'}</span>
                    </div>
                  </Marker>
                ))}
              </Map>
            </div>
          }
        />

        <Card
          width="1-3"
          title="Koffer"
          data={
            <div className="dash-device-list">
              {this.state.devices.map((d) => {
                const online =
                  d.value_created &&
                  Date.now() - new Date(d.value_created).getTime() < ONLINE_THRESHOLD_MS;
                const last = d.value_created
                  ? moment(d.value_created).format('DD.MM.YYYY HH:mm')
                  : '–';
                return (
                  <Link key={d.id} to={`/devices/${d.id}/`} className="dash-device-row">
                    <IconWell name={online ? 'online' : 'offline'} tone={online ? 'ok' : 'bad'} size={28} />
                    <div className="dash-device-meta">
                      <div className="dash-device-name">{d.name || `Koffer ${d.id}`}</div>
                      <div className="dash-device-time">{last}</div>
                    </div>
                    <Icon name="chevron" size={20} sx={{ color: 'text.disabled' }} />
                  </Link>
                );
              })}
              {this.state.devices.length === 0 && !this.state.loading && (
                <div className="dash-empty">
                  <Icon name="koffer" size={36} sx={{ opacity: 0.4 }} />
                  <span>Keine Koffer vorhanden</span>
                </div>
              )}
            </div>
          }
        />

        <Card
          width="1"
          title="Aktuelle Ereignisse"
          icon="events"
          data={
            <div className="dash-event-list">
              {this.state.events.map((ev) => (
                <div key={ev.id} className="dash-event-row">
                  <IconWell
                    name={ev.confirmed ? 'confirmed' : 'alarm'}
                    tone={ev.confirmed ? 'ok' : 'bad'}
                    size={28}
                  />
                  <div className="dash-event-meta">
                    <div className="dash-event-title">
                      {ev.device_name || 'Koffer'} · {ev.type || 'Ereignis'}
                    </div>
                    <div className="dash-event-time">
                      {ev.created ? moment(ev.created).format('DD.MM.YYYY HH:mm') : '–'}
                    </div>
                  </div>
                  <span className={`fc-chip ${ev.confirmed ? 'is-ok' : 'is-alarm'}`}>
                    {ev.confirmed ? 'Bestätigt' : 'Offen'}
                  </span>
                </div>
              ))}
              {this.state.events.length === 0 && (
                <div className="dash-empty">
                  <Icon name="inbox" size={36} sx={{ opacity: 0.4 }} />
                  <span>Keine Ereignisse</span>
                </div>
              )}
            </div>
          }
        />
      </SecuredZone>
    );
  }
}

export default Dashboard;
